import { useState, useEffect } from 'react';
import { GLOBAL_FILTER_EVENTS } from '@/features/global-filter/constants/events';

function areKeysEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((k, i) => k === b[i]);
}

/**
 * Listens to the CHANNELS_EXCLUDE custom event and keeps a list of excluded
 * channel keys up to date. Accepts an optional `overrideKeys` prop so that
 * web-component wrappers (e.g. Header) can also reflect attribute updates from
 * the MutationObserver without managing a separate sync effect.
 */
export function useExcludedChannelKeys(overrideKeys?: string[]): string[] {
  const [excludedChannelKeys, setExcludedChannelKeys] = useState<string[]>(overrideKeys || []);
  const [prevOverrideKeys, setPrevOverrideKeys] = useState(overrideKeys);

  // Derived-state pattern: sync prop → state during render, not in an effect.
  // React discards this render and immediately re-renders with the updated state.
  if (prevOverrideKeys !== overrideKeys && overrideKeys !== undefined) {
    setPrevOverrideKeys(overrideKeys);
    if (!areKeysEqual(overrideKeys, prevOverrideKeys)) {
      setExcludedChannelKeys(overrideKeys);
    }
  }

  useEffect(() => {
    const handleChannelsExclude = (e: Event) => {
      const keys: string[] = (e as CustomEvent<{ channels: string[] }>).detail?.channels || [];
      setExcludedChannelKeys(keys);
    };
    document.addEventListener(GLOBAL_FILTER_EVENTS.CHANNELS_EXCLUDE, handleChannelsExclude);
    return () => document.removeEventListener(GLOBAL_FILTER_EVENTS.CHANNELS_EXCLUDE, handleChannelsExclude);
  }, []);

  return excludedChannelKeys;
}
